/**
 * Secure Supabase Client with httpOnly Cookie Session Handling
 *
 * This module provides a Supabase client configured to use httpOnly cookies
 * for session management instead of localStorage. This provides better
 * protection against XSS attacks.
 *
 * IMPORTANT: To use this secure client, you need to:
 * 1. Deploy the auth-proxy edge function
 * 2. Set the COOKIE_DOMAIN environment variable if using subdomains
 * 3. Update imports from '@/integrations/supabase/client' to use this module
 *
 * Migration Strategy:
 * - This can be enabled gradually by replacing imports
 * - The existing localStorage-based client can run in parallel during migration
 * - Set USE_SECURE_COOKIES=true in environment to enable globally
 */

import { createClient, SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import {
  secureCookieStorage,
  signInWithCookies,
  signUpWithCookies,
  signOutWithCookies,
} from "./secureCookieStorage";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/**
 * Secure cookie auth feature flag
 *
 * Disabled globally: all environments use the standard localStorage-based session
 * handling from the default backend client.
 */
export const USE_SECURE_COOKIES = false;


/**
 * Create a Supabase client with secure cookie-based session handling
 */
function createSecureSupabaseClient(): SupabaseClient<Database> {
  return createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      storage: secureCookieStorage,
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false, // We handle this via auth-proxy
      flowType: "pkce", // Use PKCE for additional security
    },
    global: {
      fetch: (url, options = {}) => {
        // Ensure credentials are included for all requests
        return fetch(url, {
          ...options,
          credentials: "include",
        });
      },
    },
  });
}

// Standard client is just the singleton from the integrations folder
// This ensures we use the SAME instance everywhere

/**
 * The Supabase client - uses secure cookies if enabled, otherwise the standard singleton
 */
export const supabaseSecure: SupabaseClient<Database> = USE_SECURE_COOKIES
  ? createSecureSupabaseClient()
  : supabase;

/**
 * Secure authentication functions
 * These use the auth-proxy edge function to manage httpOnly cookies
 */
export const secureAuth = {
  /**
   * Sign in with email and password
   * Sets httpOnly cookies via auth-proxy
   */
  signIn: async (email: string, password: string) => {
    if (!USE_SECURE_COOKIES) {
      // Fallback to standard Supabase auth
      return supabaseSecure.auth.signInWithPassword({ email, password });
    }

    const result = await signInWithCookies(email, password);

    if (result.success) {
      // Clear the cache and fetch fresh session from cookies
      secureCookieStorage.clearCache();
      
      // Get the session which will trigger onAuthStateChange
      const { data: sessionData } = await supabaseSecure.auth.getSession();
      
      return { 
        data: { 
          user: result.user || sessionData?.session?.user, 
          session: sessionData?.session 
        }, 
        error: null 
      };
    }

    return {
      data: { user: null, session: null },
      error: { message: result.error || "Authentication failed", status: 401 },
    };
  },

  /**
   * Sign up with email, password, and name
   */
  signUp: async (email: string, password: string, name: string) => {
    if (!USE_SECURE_COOKIES) {
      // Fallback to standard Supabase auth
      return supabaseSecure.auth.signUp({
        email,
        password,
        options: { data: { name } },
      });
    }

    const result = await signUpWithCookies(email, password, name);

    if (result.success) {
      return { data: { user: result.user, session: null }, error: null };
    }

    return {
      data: { user: null, session: null },
      error: { message: result.error || "Signup failed", status: 400 },
    };
  },

  /**
   * Sign out - clears httpOnly cookies
   */
  signOut: async () => {
    if (!USE_SECURE_COOKIES) {
      return supabaseSecure.auth.signOut();
    }

    const result = await signOutWithCookies();
    secureCookieStorage.clearCache();

    if (result.success) {
      return { error: null };
    }

    return { error: { message: result.error || "Sign out failed" } };
  },

  /**
   * Get current session
   */
  getSession: async () => {
    return supabaseSecure.auth.getSession();
  },

  /**
   * Get current user
   */
  getUser: async () => {
    return supabaseSecure.auth.getUser();
  },

  /**
   * Listen for auth state changes
   */
  onAuthStateChange: (
    callback: Parameters<typeof supabaseSecure.auth.onAuthStateChange>[0]
  ) => {
    return supabaseSecure.auth.onAuthStateChange(callback);
  },
};

/**
 * Hook to use the appropriate Supabase client based on security mode
 */
export function getSupabaseClient(): SupabaseClient<Database> {
  return supabaseSecure;
}

export default supabaseSecure;
